import React, { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { RiSearchLine } from "react-icons/ri"
import { BiDownload } from "react-icons/bi"
import { toast } from 'react-toastify';

import {
    Box,
    Grid,
    TextField,
    CardContent,
    FormControl,
} from '@mui/material'
import { getFirstDayOfPreviousMonth, getLastDayOfPreviousMonth } from '../../utils/dateUtils'

import * as yup from "yup"
import { yupResolver } from '@hookform/resolvers/yup'

import { CardStyled, DownloadButton } from './style'
import TitleCard from '../../components/Title'
import SelectStyled from '../../components/Select'
import Button from '../../components/DefaultButton'
import DatePicker from '../../components/DatePicker'
import TableNfce from '../../components/Tables/TableNfce'
import {
    getNfceDownloadJob,
    getNfces,
    createNfceDownloadJob,
} from '../../services/nfceService'
import api from '../../services/api'
import CircularProgress from '../../components/CircularProgress'
import CircularProgressDisabled from '../../components/CircularProgressDisabled'
import { Title } from '../../components/Title/style';

const validationSchema = yup.object({
    clienteId: yup.string().required("Selecione o cliente!"),
    dataDe: yup.date().required("Insira uma data!"),
    dataAte: yup.date().required("Insira uma data!"),
})

export default function Nfce() {
    const pollTimeoutRef = useRef(null)
    const [nfces, setNfces] = useState([])
    const [count, setCount] = useState(0)
    const [page, setPage] = useState(0)
    const [total, setTotal] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(0)
    const [isLoading, setIsLoading] = useState(false)
    const [downloading, setDownloading] = useState(false)

    useEffect(() => {
        return () => {
            if (pollTimeoutRef.current) {
                clearTimeout(pollTimeoutRef.current)
            }
        }
    }, [])

    function clearPollingTimeout() {
        if (pollTimeoutRef.current) {
            clearTimeout(pollTimeoutRef.current)
            pollTimeoutRef.current = null
        }
    }

    function createIdempotencyKey() {
        if (window.crypto?.randomUUID) {
            return window.crypto.randomUUID()
        }

        return `nfce-download-${Date.now()}-${Math.random().toString(16).slice(2)}`
    }

    function resolveDownloadUrl(downloadUrl) {
        if (!downloadUrl) {
            return null
        }

        if (/^https?:\/\//i.test(downloadUrl)) {
            return downloadUrl
        }

        return `${api.defaults.baseURL}${downloadUrl}`
    }

    function startNativeBrowserDownload(downloadUrl) {
        const resolvedUrl = resolveDownloadUrl(downloadUrl)

        if (!resolvedUrl) {
            throw new Error('URL de download não disponível')
        }

        window.location.assign(resolvedUrl)
    }

    async function waitForNfceDownloadJob(clienteId, jobId, idempotencyKey) {
        while (true) {
            const response = await getNfceDownloadJob(clienteId, jobId, idempotencyKey)
            const job = response.data

            if (job.status === 'COMPLETED') {
                return job
            }

            if (job.status === 'FAILED') {
                throw new Error(job.errorMessage || 'Falha ao gerar o arquivo para download')
            }

            await new Promise((resolve) => {
                pollTimeoutRef.current = setTimeout(() => {
                    pollTimeoutRef.current = null
                    resolve()
                }, 3000)
            })
        }
    }

    async function onSubmit(values, page = 0) {
        setIsLoading(true)
        setNfces([])
        try {
            const { clienteId, dataDe, dataAte } = values
            const response = await getNfces(clienteId, dataDe, dataAte, page)
            const _nfces = response.data.content
            setPage(page);
            setNfces(_nfces)
            setCount(response.data.totalElements)
            setTotal(response.data.total)
            setRowsPerPage(response.data.size)
        } catch (error) {
            toast.error(error.response?.data?.message || error.message)
        } finally {
            setIsLoading(false)
        }
    }

    async function onDownload(values) {
        setDownloading(true)
        clearPollingTimeout()
        try {
            const { clienteId, dataDe, dataAte } = values
            const idempotencyKey = createIdempotencyKey()
            const createJobResponse = await createNfceDownloadJob(clienteId, dataDe, dataAte, idempotencyKey)
            const job = await waitForNfceDownloadJob(clienteId, createJobResponse.data.id, idempotencyKey)
            startNativeBrowserDownload(job.downloadUrl)
        } catch (error) {
            toast.error(error.response?.data?.message || error.message)
        } finally {
            clearPollingTimeout()
            setDownloading(false)
        }
    }

    const { control, handleSubmit, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            clienteId: "",
            dataDe: getFirstDayOfPreviousMonth(),
            dataAte: getLastDayOfPreviousMonth(),
        }
    })

    return (
        <>
            <FormControl
                onSubmit={handleSubmit((values) => onSubmit(values))}
                sx={{
                    width: '100%',
                }}
            >
                <CardStyled>
                    <CardContent>
                        <Box
                            component="form"
                        >
                            <Grid
                                container
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                <TitleCard
                                    pageName="NFC-e"
                                    description="(Nota Fiscal do Consumidor Eletrônica)"
                                />
                                <Grid
                                    container
                                    spacing={2}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'flex-end',
                                        padding: '1em 0'
                                    }}
                                >
                                    <Grid
                                        item
                                        sm={5}
                                    >
                                        <Controller
                                            control={control}
                                            name="clienteId"
                                            render={({ field }) => (
                                                <SelectStyled
                                                    value={field.value}
                                                    onChange={(e) => {
                                                        setNfces([])
                                                        field.onChange(e)
                                                    }}
                                                    inputRef={field.ref}
                                                    displayEmpty
                                                    label="Clientes"
                                                    error={!!errors.clienteId}
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        sm={5}
                                    >
                                        <DatePicker
                                            control={control}
                                            renderInput={(params) =>
                                                <TextField
                                                    {...params}
                                                    error={!!errors.dataDe || !!errors.dataAte}
                                                />
                                            }
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        sm={2}
                                    >
                                        <Button
                                            type="submit"
                                            label="Buscar"
                                            startIcon={
                                                <RiSearchLine />
                                            }
                                        />
                                    </Grid>
                                    <Grid
                                        item
                                        xs={4}
                                        sm={4}
                                        sx={{
                                            paddingTop: '0px!important',
                                            color: 'red'
                                        }}
                                    >
                                        <span>
                                            {errors.clienteId?.message}
                                        </span>
                                    </Grid>
                                    <Grid
                                        item
                                        xs={8}
                                        sm={8}
                                        sx={{
                                            paddingTop: '0px!important',
                                            color: 'red'
                                        }}
                                    >
                                        <span>
                                            {!!errors.dataDe ? !!errors.dataDe && 'Insira uma data válida para os dois campos!' : !!errors.dataAte?.message && 'Insira uma data válida para os dois campos!'}
                                        </span>
                                    </Grid>
                                </Grid>
                                <Grid
                                    item
                                    sm={2}
                                >
                                    <DownloadButton
                                        label="Download"
                                        onClick={handleSubmit(onDownload)}
                                        startIcon={
                                            downloading ? <CircularProgressDisabled /> : <BiDownload />
                                        }
                                        disabled={nfces.length === 0 || downloading}
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </CardContent>
                </CardStyled>
                {
                    isLoading && (<CircularProgress />)
                }

                {
                    nfces.length === 0 &&
                    <Grid
                        container
                    >
                        <Grid
                            item
                            xs={12}
                            sm={12}
                            md={12}
                            lg={12}
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            {
                                !isLoading ?
                                    <Title>
                                        Nenhum registro encontrado...
                                    </Title> :
                                    null
                            }
                        </Grid>
                    </Grid>
                }

                {
                    nfces.length > 0 &&
                    <TableNfce
                        nfces={nfces}
                        count={count}
                        page={page}
                        onPageChange={(event, nextPage) => {
                            handleSubmit((values) => onSubmit(values, nextPage))()
                        }}
                        rowsPerPage={rowsPerPage}
                        total={total}
                    />
                }
            </FormControl>
        </>
    )
}
